import {
  Box,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { useRef } from 'react';
import type { ReactNode, SyntheticEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { RefHandles } from 'react-spring-bottom-sheet/dist/types';
import { AnimatedTimelineControl } from 'components/AnimatedTimelineControl';
import type { TimelinePlayback } from 'features/mapping/mapping.types';
import type {
  MapTimelineLayer,
  TimelineLayerId,
} from 'features/mapping/mapping.constants';
import { TimelineModal } from 'features/mapping/components/timeline/TimelineModal';
import { TimelineAttributionLink } from 'features/mapping/components/timeline/TimelineAttributionLink';
import { TIMELINE_DESKTOP_LEGEND_HEIGHT } from 'features/mapping/components/timeline/timeline.constants';
import {
  TimelineLayerMenu,
  TimelineLayerMenuList,
} from 'features/mapping/components/timeline/TimelineLayerMenu';
import {
  TimelineMobileLegend,
  TimelineMobileLegendMode,
} from 'features/mapping/components/timeline/TimelineMobileLegend';
import {
  TimelineSheet,
  collapseTimelineSheet,
} from 'features/mapping/components/timeline/TimelineSheet';

export type TimelineOverlayProps = {
  /** The playback details for the active timeline layer. */
  playback: TimelinePlayback;
  /** The active timeline layer. */
  layer: MapTimelineLayer;
  /** Available timeline layers to be shown in a menu. */
  layers: MapTimelineLayer[];
  onLayerChange: (layerId: TimelineLayerId) => void;
  legend?: ReactNode;
  legendItemCount?: number;
  onClose?: () => void;
  /** Portal mount for `TimelineModal`. Defaults to `document.body`. */
  container?: HTMLElement | null;
  /**
   * Mobile legend positioning.
   * - `viewport` — fixed below the app header (production map)
   * - `contained` — absolute at the top of a positioned parent (Storybook phone frame)
   */
  mobileLegendMode?: TimelineMobileLegendMode;
};

// Prevent drags on the timeline slider from moving the bottom sheet.
const stopSheetDragOnSlider = (event: SyntheticEvent): void => {
  if ((event.target as Element).closest('.MuiSlider-root')) {
    event.stopPropagation();
  }
};

/** Timeline control overlay used by every timeline layer. */
export const TimelineOverlay = ({
  playback,
  layer,
  layers,
  onLayerChange,
  legend,
  legendItemCount,
  onClose,
  container,
  mobileLegendMode = 'viewport',
}: TimelineOverlayProps): JSX.Element => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('tablet'));
  const sheetRef = useRef<RefHandles | null>(null);
  const hasMultipleLayers = layers.length > 1;

  const handlePlayPause = (): void => {
    // Starting playback collapses the mobile sheet so the map is in view
    if (!playback.isPlaying) {
      collapseTimelineSheet(sheetRef);
    }
    playback.onPlayPause();
  };

  const handleSheetLayerSelect = (layerId: TimelineLayerId): void => {
    collapseTimelineSheet(sheetRef);
    if (layerId !== layer.id) {
      onLayerChange(layerId);
    }
  };

  const title = <Typography variant="h3">{t(layer.titleKey)}</Typography>;
  const attribution = <TimelineAttributionLink {...layer.attribution} />;

  const closeButton = onClose && (
    <IconButton
      onClick={onClose}
      aria-label={t('common.close')}
      sx={{
        width: 36,
        height: 36,
        ml: isMobile ? 0 : 'auto',
        backgroundColor: theme.vars.palette.background.secondary,
        color: 'inherit',
        '&:hover': {
          backgroundColor: 'rgba(255, 255, 255, 0.3)',
        },
      }}
    >
      <Close fontSize="small" />
    </IconButton>
  );

  const timelineControl = (
    <AnimatedTimelineControl
      {...playback}
      onPlayPause={handlePlayPause}
      markIntervalMinutes={layer.markIntervalMinutes}
      markLabelIntervalMinutes={layer.markLabelIntervalMinutes}
      emphasizeEndLabel={layer.emphasizeEndLabel}
      labels={{
        start: t(layer.timelineLabelKeys.start),
        end: t(layer.timelineLabelKeys.end),
      }}
    />
  );

  if (isMobile) {
    return (
      <>
        {legend && (
          <TimelineMobileLegend
            mode={mobileLegendMode}
            itemCount={legendItemCount}
          >
            {legend}
          </TimelineMobileLegend>
        )}

        <TimelineSheet
          sheetRef={sheetRef}
          expandable={hasMultipleLayers}
          header={
            <>
              <Stack
                direction="row"
                alignItems="center"
                gap={0.5}
                sx={{ pt: 1, pr: 1, pl: 2 }}
              >
                <Stack sx={{ flexGrow: 1, alignItems: 'start', gap: 0.5 }}>
                  {title}
                  {attribution}
                </Stack>
                {closeButton}
              </Stack>
              <Box
                sx={{ pt: 1 }}
                onPointerDown={stopSheetDragOnSlider}
                onMouseDown={stopSheetDragOnSlider}
                onTouchStart={stopSheetDragOnSlider}
              >
                {timelineControl}
              </Box>
            </>
          }
        >
          <Box sx={{ p: 2 }}>
            <TimelineLayerMenuList
              layers={layers}
              selectedLayerId={layer.id}
              onSelect={handleSheetLayerSelect}
              isMobile
            />
          </Box>
        </TimelineSheet>
      </>
    );
  }

  return (
    <TimelineModal container={container}>
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          pt: 1,
          pr: 1,
          pb: 0,
          pl: 2,
          gap: 0.5,
        }}
      >
        <Stack
          direction="row"
          spacing={1.5}
          useFlexGap
          sx={{ flexGrow: 1, alignItems: 'baseline' }}
        >
          {hasMultipleLayers ? (
            <TimelineLayerMenu
              layers={layers}
              selectedLayerId={layer.id}
              onChange={onLayerChange}
            />
          ) : (
            title
          )}
          {attribution}
        </Stack>
        {closeButton}
      </DialogTitle>

      <DialogContent sx={{ p: 0, paddingTop: 1 }}>
        {timelineControl}
        {legend && (
          <Box sx={{ height: TIMELINE_DESKTOP_LEGEND_HEIGHT }}>{legend}</Box>
        )}
      </DialogContent>
    </TimelineModal>
  );
};
